/* eslint-disable no-console */
import {
    ArtistAPI,
    IRequestDeleteArtist,
    ArtistI,
    IRequestCreateArtist,
    IRequestUpdateArtist,
    NftAPI,
    NFTI,
    ESortBy,
    DashboardAPI,
} from '@tacans/shared-marketplace-layer';
import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createArtistFetchFilterPayload } from 'src/features/common/artist/artist.utils';

/**
 * Utils
 */
import { createFilterFieldsPayload } from 'src/features/common/explore/explore-api.utils';
import { useAppDispatch, useAppSelector } from 'src/hooks/app';
import { useIsMounted } from 'src/hooks/useIsMounted';

/**
 * Hooks
 */
import { useNotifications } from 'src/services/notifications/use-notifications';
import { selectArtist } from './ducks/artist.selectors';
import { actions as artistActions } from './ducks/artist.slice';
import { artistFetchThunk } from './ducks/artist.thunk';

export const useFetchArtist = () => {
    const [artist, setArtist] = useState<ArtistI>();
    const isMounted = useIsMounted();

    const fetchArtist = useCallback(
        async (artistId: string) => {
            try {
                const response = await ArtistAPI.fetchById(artistId);

                if (response?.data.responseMeta?.error?.code) {
                    throw new Error(response?.data?.responseMeta?.error?.code);
                }

                isMounted(() => {
                    setArtist(response?.data.artist);
                });
            } catch (error) {
                console.error('useFetchArtist', error);
            }
        },
        [isMounted]
    );

    return { artist, fetchArtist };
};

interface IInitialFetchData {
    ownerId: string;
    universeId?: string;
    limit?: number;
    infinity?: boolean;
}

export const useFetchArtistsByFilter = ({ ownerId, universeId, limit, infinity }: IInitialFetchData) => {
    const [artists, setArtists] = useState<ArtistI[]>([]);
    const [payload, setPayload] = useState(
        createArtistFetchFilterPayload({
            filter: { ownerId },
            pager: { limit },
        })
    );

    const [total, setTotal] = useState(0);
    const [isLoading, setLoading] = useState(true);
    const isMounted = useIsMounted();

    const changePage = useCallback((page: number) => {
        setPayload((prev) => ({
            ...prev,
            pager: {
                ...prev.pager,
                page,
            },
        }));
    }, []);

    const fetchArtists = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ArtistAPI.fetchByFilter(payload);
            if (response?.data.responseMeta?.error?.code) {
                throw new Error(response?.data.responseMeta?.error?.code);
            }

            isMounted(() => {
                setArtists((prev) => (infinity ? [...prev, ...response?.data?.list] : response?.data?.list));
                setTotal(+response?.data?.pager?.totalCount ?? 0);
            });
        } catch (error) {
            console.error('useFetchArtistsByFilter', error);
        }
        setLoading(false);
    }, [isMounted, payload, infinity]);

    return { artists, fetchArtists, isLoading, changePage, total, payload };
};

export const useFetchArtistsCollectibles = () => {
    const [artistCollectibles, setArtistCollectibles] = useState<Record<string, NFTI[]>>({});
    const [loading, setLoading] = useState(true);

    const isMounted = useIsMounted();

    const fetchArtistCollectibles = useCallback(
        async (artistIds: string[], limit = 4) => {
            try {
                setLoading(true);
                const result: Record<string, NFTI[]> = Object.fromEntries(artistIds.map((id) => [id, []]));
                const response = await DashboardAPI.fetchLatestArtistsNfts({ artistIds, limit });

                if (response?.data.responseMeta?.error?.messages) {
                    throw new Error(response?.data.responseMeta?.error?.code);
                }

                isMounted(() => {
                    const { nfts = [] } = response.data;
                    nfts.forEach((nft) => {
                        const { artistId } = nft;
                        if (artistId && artistId in result) {
                            result[artistId].push(nft);
                        }
                    });
                    setArtistCollectibles((prev) => ({ ...prev, ...result }));
                });
            } catch (error) {
                const errors = (error as AxiosError)?.response?.data?.errors;
                console.error('useFetchArtistsCollectibles', errors);
            }
        },
        [isMounted]
    );

    return {
        fetchArtistCollectibles,
        artistCollectibles,
        loading,
    };
};

export const useFetchArtistCollectibles = () => {
    const [artistCollectibles, setArtistCollectibles] = useState<NFTI[]>([]);
    const isMounted = useIsMounted();

    const fetchArtistCollectibles = useCallback(
        async (artistId: string, limit = 200) => {
            const response = await NftAPI.fetchByFilter({
                sorting: {
                    sortBy: ESortBy.Date,
                    ascending: false,
                },
                filter: createFilterFieldsPayload({
                    artistId: [artistId],
                }),
                pager: { limit, page: 0 },
            });

            isMounted(() => {
                setArtistCollectibles(response.data?.list ?? []);
            });
        },
        [isMounted]
    );

    return {
        fetchArtistCollectibles,
        artistCollectibles,
    };
};

export const useCreateArtist = () => {
    const [errors, setErrors] = useState<any>([]);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [artistId, setArtistId] = useState<string>();
    const { error: errorNotification } = useNotifications();
    const isMounted = useIsMounted();

    const createArtist = useCallback(
        async (data: IRequestCreateArtist) => {
            try {
                setLoading(true);

                const { ...artistData } = data;

                const response = await ArtistAPI.create(artistData);

                if (response?.data.responseMeta?.error?.messages) {
                    isMounted(() => {
                        setErrors(response?.data.responseMeta?.error?.messages);
                    });

                    const error = response?.data.responseMeta?.error?.messages[0];
                    error && errorNotification({ text: error });

                    throw new Error(response?.data.responseMeta?.error?.code);
                }

                isMounted(() => {
                    setErrors([]);
                    setSuccess(true);
                    setArtistId(response?.data?.id);
                });
            } catch (error) {
                const errors = (error as AxiosError)?.response?.data?.errors;

                if (errors?.length) {
                    isMounted(() => {
                        setErrors(errors);
                    });

                    errorNotification({ text: errors[0].errorMessage });
                }

                console.error('useCreateArtist', error);
            } finally {
                setLoading(false);
            }
        },
        [errorNotification, isMounted]
    );

    return { errors, success, loading, createArtist, artistId };
};

export const useUpdateArtist = () => {
    const [errors, setErrors] = useState<any>([]);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const { error: errorNotification } = useNotifications();
    const isMounted = useIsMounted();

    const updateArtist = useCallback(
        async (data: IRequestUpdateArtist) => {
            try {
                setLoading(true);

                const response = await ArtistAPI.update(data);

                if (response?.data.responseMeta?.error?.messages) {
                    isMounted(() => {
                        setErrors(response?.data.responseMeta?.error?.messages);
                    });

                    const error = response?.data.responseMeta?.error?.messages[0];
                    error && errorNotification({ text: error });

                    throw new Error(response?.data.responseMeta?.error?.code);
                }

                isMounted(() => {
                    setErrors([]);
                    setSuccess(true);
                });
            } catch (error) {
                console.error('useUpdateArtist', error);
            } finally {
                setLoading(false);
            }
        },
        [errorNotification, isMounted]
    );

    return { errors, success, loading, updateArtist };
};

export const useUpdateArtistAvatar = () => {
    const updateAvatar = useCallback(async (artistId: string, avatar: File) => {
        const avatarData = new FormData();
        avatarData.append('file', avatar);

        try {
            const response = await ArtistAPI.updateAvatar(artistId, avatarData);

            if (response?.data.responseMeta?.error?.code) {
                throw new Error(response?.data.responseMeta?.error?.code);
            }
        } catch (error) {
            console.log('useUpdateArtistAvatar', error);
        }
    }, []);

    return { updateAvatar };
};

export const useDeleteArtist = (name: string) => {
    const { t } = useTranslation(['modals']);
    const [artistDeleted, setArtistDeleted] = useState(false);
    const { success: successNotification } = useNotifications();
    const isMounted = useIsMounted();

    const deleteArtist = useCallback(
        async (data: IRequestDeleteArtist) => {
            const response = await ArtistAPI.delete(data);

            if (response?.data.responseMeta?.error?.messages) {
                isMounted(() => {
                    throw new Error(response?.data.responseMeta?.error?.messages[0] || 'pew');
                });

                return;
            }

            successNotification({
                text: t('deleteCreator.deleted', { name }),
            });

            if (response?.data.responseMeta?.error?.messages) {
                isMounted(() => {
                    throw new Error(response?.data.responseMeta?.error?.messages[0] || 'pew');
                });

                return;
            }

            isMounted(() => {
                setArtistDeleted(true);
            });
        },
        [successNotification, t, name, isMounted]
    );

    return { deleteArtist, artistDeleted };
};

export const useDeleteArtistBanner = () =>
    useCallback(async (id: string) => {
        const response = await ArtistAPI.removeBanner(id);

        return response.data.responseMeta.status === 'OK';
    }, []);

export const useFetchArtistByUniverse = () => {
    const [artists, setArtists] = useState<ArtistI[]>();
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState<any>();
    const isMounted = useIsMounted();

    const fetchArtists = useCallback(
        async (universeId) => {
            setLoading(true);
            try {
                const response = await ArtistAPI.fetchByUniverse(universeId);

                if (response?.data.responseMeta?.error?.code) {
                    throw new Error(response?.data.responseMeta?.error?.code);
                }

                isMounted(() => {
                    setArtists(response?.data?.artists);
                });
            } catch (error) {
                isMounted(() => setError(error));
            }
            setLoading(false);
        },
        [isMounted]
    );

    const resetError = useCallback(() => setError(null), []);

    return { artists, fetchArtists, error, resetError, isLoading };
};

export const useArtistFetchThunk = () => {
    const dispatch = useAppDispatch();

    return useCallback(
        (id: string) => {
            dispatch(artistFetchThunk(id));
        },
        [dispatch]
    );
};

export const useClearArtistThunk = () => {
    const dispatch = useAppDispatch();

    return useCallback(() => {
        dispatch(artistActions.clearArtist());
    }, [dispatch]);
};

export const useArtist = () => useAppSelector(selectArtist);
